import { Component, Input, OnInit } from '@angular/core';
import {
  CheckInEntryMethod,
  CustomerFilterMatchModeKey,
  CustomerFilters,
  CustomerTag,
  Gender,
} from '../../../../../../models/customer';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { Observable, OperatorFunction } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { propExistsAndIsNonEmpty, searchList, searchStringList } from '../../../../../../utils';
import { EventType} from '../../../../../../models/event-type';
import { Proficiency } from '../../../../../../models/proficiency';
import { Association } from '../../../../../../models/association';
import { Challenge, ChallengeStatus } from '../../../../../../models/challenge';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';
import { CapitanLocation } from '../../../../../../models/location';
import { FilterMatchMode } from '../../../../../../models/filter-match-mode';

@Component({
  selector: 'app-customer-filters-modal',
  templateUrl: './customer-filters-modal.component.html',
  styleUrls: ['./customer-filters-modal.component.css']
})
export class CustomerFiltersModalComponent implements OnInit {

  @Input() inputCustomerFilters: CustomerFilters;
  @Input() tags: CustomerTag[];
  @Input() eventTypes: EventType[];
  @Input() proficiencies: Proficiency[];
  @Input() associations: Association[];
  @Input() challenges: Challenge[];
  @Input() locations: CapitanLocation[];
  @Input() savedCallback: (newFilters: CustomerFilters) => undefined;

  public filters: CustomerFilters;
  public hasReservationEventType: EventType = null;
  public hasReservationMinDate = '';
  public hasReservationMaxDate = '';
  public hadCheckInMinDate = '';
  public hadCheckInMaxDate = '';
  public hadCheckInEntryMethod: CheckInEntryMethod = null;
  public hasChallengeTypes: Challenge[] = [];
  // Challenge IDs present in the has_challenge filter that aren't in the
  // loaded challenge list (deleted via admin, or beyond the fetch cap). Kept
  // and re-serialized on submit so opening the modal never silently narrows
  // the filter; rendered as "Unknown challenge" entries the user can remove.
  public hasChallengeUnresolvedIds: number[] = [];
  // Staged like the other challenge sub-fields (written to the filters object
  // only on submit, in loadChallengeFieldsIntoFilters).
  public hasChallengeMatchMode: FilterMatchMode = FilterMatchMode.all;
  public hasChallengeIsComplete: boolean | null = null;
  public hasChallengeMinCompletedDate = '';
  public hasChallengeMaxCompletedDate = '';

  public tagSearchInput = '';
  public eventTypeSearchInput = '';
  public proficiencySearchInput = '';
  public associationSearchInput = '';
  public challengeSearchInput = '';

  public readonly Gender = Gender;
  public readonly EventTypeStatus = EventTypeStatus;
  public readonly ChallengeStatus = ChallengeStatus;
  public readonly CheckInEntryMethod = CheckInEntryMethod;
  public readonly FilterMatchMode = FilterMatchMode;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService
  ) {}

  ngOnInit() {
    // Get a deep copy of the input filters. This ensures that the filters are
    // only updated once they are submitted, rather than as the user edits them.
    this.filters = {};

    for (const key of Object.keys(this.inputCustomerFilters)) {
      this.filters[key] = this.inputCustomerFilters[key];
    }

    this.extractReservationFieldsFromFilters();
    this.extractCheckInFieldsFromFilters();
    this.extractChallengeFieldsFromFilters();
  }

  public handleSubmit() {
    // created_at / first_membership_timestamp / latest_check_in_timestamp are
    // edited via <app-date-range-filter>, which emits `undefined` (never '')
    // for cleared values, so they need no empty-string coercion here.
    if (this.filters.last_activity_timestamp_min === '') {
      this.filters.last_activity_timestamp_min = undefined;
    }
    if (this.filters.last_activity_timestamp_max === '') {
      this.filters.last_activity_timestamp_max = undefined;
    }

    this.loadReservationFieldsIntoFilters();
    this.loadCheckInFieldsIntoFilters();
    this.loadChallengeFieldsIntoFilters();

    this.savedCallback(this.filters);
    this.activeModal.close();
  }

  public handleClearAll() {
    // Clear all filters except for "search" (which is not edited in this
    // modal).
    this.filters = {search: this.filters.search};
    this.clearReservationFields();
    this.clearCheckInFields();
    this.clearChallengeFields();
  }

  public handleHasActiveMembershipChange() {
    if (!this.filters.has_active_membership) {
      this.filters.member_active_membership_name = undefined;
    }
  }

  public addTag(tagName: string) {
    if (!tagName) { return; }

    let tagList = [];
    if (this.filters.tags) {
      tagList = this.filters.tags.split(',');
    }

    tagList.push(tagName);

    this.filters.tags = tagList.join(',');

    this.tagSearchInput = '';
  }

  public removeTag(tagName: string) {
    const tagList = this.filters.tags.split(',');

    tagList.splice(tagList.indexOf(tagName), 1);

    this.filters.tags = tagList.join(',');

    if (this.filters.tags === '') {
      this.clearTags();
    }
  }

  public clearTags() {
    this.filters.tags = undefined;
    this.filters.tags_match = undefined;
  }

  public addProficiency(proficiency: Proficiency) {
    let profList = [];
    if (this.filters.has_proficiencies) {
      profList = this.filters.has_proficiencies.split(',');
    }

    profList.push(proficiency.id.toString());

    this.filters.has_proficiencies = profList.join(',');

    this.proficiencySearchInput = '';
  }

  public removeProficiency(proficiencyIdStr: string) {
    const profList = this.filters.has_proficiencies.split(',');

    profList.splice(profList.indexOf(proficiencyIdStr), 1);

    this.filters.has_proficiencies = profList.join(',');

    if (this.filters.has_proficiencies === '') {
      this.clearProficiencies();
    }
  }

  public clearProficiencies() {
    this.filters.has_proficiencies = undefined;
    this.filters.has_proficiencies_match = undefined;
  }

  public getMatchMode(matchKey: CustomerFilterMatchModeKey): FilterMatchMode {
    return this.filters[matchKey] || FilterMatchMode.all;
  }

  public setMatchMode(matchKey: CustomerFilterMatchModeKey, matchMode: FilterMatchMode) {
    // "all" is the backend default, so it doesn't need to travel as a query
    // parameter (and shouldn't clutter saved views / shared URLs).
    this.filters[matchKey] = (
      matchMode === FilterMatchMode.all ? undefined : matchMode
    );
  }

  public addAssociation(assoc: Association) {
    let profList = [];
    if (this.filters.has_association_memberships) {
      profList = this.filters.has_association_memberships.split(',');
    }

    profList.push(assoc.id.toString());

    this.filters.has_association_memberships = profList.join(',');

    this.associationSearchInput = '';
  }

  public removeAssociation(assocIdStr: string) {
    const profList = this.filters.has_association_memberships.split(',');

    profList.splice(profList.indexOf(assocIdStr), 1);

    this.filters.has_association_memberships = profList.join(',');

    if (this.filters.has_association_memberships === '') {
      this.clearAssociations();
    }
  }

  public clearAssociations() {
    this.filters.has_association_memberships = undefined;
    this.filters.has_association_memberships_match = undefined;
  }

  public handleTagsTypeahead: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.searchTags(term).slice(0, 10))
    )

  public handleEventTypeTypeahead: OperatorFunction<string, readonly EventType[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.searchEventTypes(term).slice(0, 10))
    )

  public handleProficienciesTypeahead: OperatorFunction<string, readonly Proficiency[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.searchProficiencies(term).slice(0, 10))
    )

  public handleAssociationsTypeahead: OperatorFunction<string, readonly Association[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.searchAssociations(term).slice(0, 10))
    )

  public handleChallengeTypeahead: OperatorFunction<string, readonly Challenge[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.searchChallenges(term).slice(0, 10))
    )

  public eventTypeTypeaheadFormatter(eventType: EventType) {
    if (eventType.status === EventTypeStatus.retired) {
      return eventType.name + ' (Retired)';
    } else {
      return eventType.name;
    }
  }

  public proficiencyTypeaheadFormatter(proficiency: Proficiency) {
    return proficiency.name;
  }

  public associationTypeaheadFormatter(assoc: Association) {
    return assoc.name;
  }

  public challengeTypeaheadFormatter(challenge: Challenge) {
    if (challenge.status === ChallengeStatus.retired) {
      return challenge.name + ' (Retired)';
    } else {
      return challenge.name;
    }
  }

  public clearReservationFields() {
    this.hasReservationEventType = null;
    this.hasReservationMinDate = '';
    this.hasReservationMaxDate = '';
  }

  public clearCheckInFields() {
    this.hadCheckInMinDate = '';
    this.hadCheckInMaxDate = '';
    this.hadCheckInEntryMethod = null;
  }

  public clearChallengeFields() {
    this.hasChallengeTypes = [];
    this.hasChallengeUnresolvedIds = [];
    this.hasChallengeMatchMode = FilterMatchMode.all;
    this.hasChallengeIsComplete = null;
    this.hasChallengeMinCompletedDate = '';
    this.hasChallengeMaxCompletedDate = '';
  }

  public get hasChallengeSelections(): boolean {
    return (
      (this.hasChallengeTypes.length > 0) ||
      (this.hasChallengeUnresolvedIds.length > 0)
    );
  }

  public addChallenge(challenge: Challenge) {
    if (this.hasChallengeTypes.some(existing => existing.id === challenge.id)) {
      this.challengeSearchInput = '';
      return;
    }

    this.hasChallengeTypes = [...this.hasChallengeTypes, challenge];

    this.challengeSearchInput = '';
  }

  public removeChallenge(challenge: Challenge) {
    this.hasChallengeTypes = this.hasChallengeTypes.filter(
      existing => existing.id !== challenge.id
    );
  }

  public removeUnresolvedChallengeId(unresolvedId: number) {
    this.hasChallengeUnresolvedIds = this.hasChallengeUnresolvedIds.filter(
      existing => existing !== unresolvedId
    );
  }

  public getProficiencyNameForIdString(proficiencyIdStr: string) {
    const proficiencyIdCleaned = proficiencyIdStr.trim();

    for (const proficiency of this.proficiencies) {
      if (proficiency.id.toString() === proficiencyIdCleaned) {
        return proficiency.name;
      }
    }

    return '<unknown>';
  }

  public getAssociationNameForIdString(assocIdStr: string) {
    const assocIdCleaned = assocIdStr.trim();

    for (const assoc of this.associations) {
      if (assoc.id.toString() === assocIdCleaned) {
        return assoc.name;
      }
    }

    return '<unknown>';
  }

  private searchTags(term: string): string[] {
    if (term.trim().length === 0) { return []; }

    const tagOptions = this.tags.map(tag => tag.name);

    // Filter out tags which have already been selected.
    if (this.filters.tags) {
      for (const selectedTag of this.filters.tags.split(',')) {
        const tagOptionIndex = tagOptions.indexOf(selectedTag);

        if (tagOptionIndex >= 0) {
          tagOptions.splice(tagOptionIndex, 1);
        }
      }
    }

    return searchStringList(
      term,
      tagOptions
    );
  }

  private searchEventTypes(term: string): EventType[] {
    return searchList<EventType>(
      term,
      this.eventTypes.map((eventType) => {
        return {
          value: eventType,
          searchableText: eventType.name
        };
      })
    );
  }

  private searchProficiencies(term: string): Proficiency[] {
    if (term.trim().length === 0) { return []; }

    // Only search proficiencies which have not already been selected.
    const selectedProficiencyIds = [];
    if (this.filters.has_proficiencies) {
      for (const selectedProfIdStr of this.filters.has_proficiencies.split(',')) {
        const selectedProfId = parseInt(selectedProfIdStr, 10);
        if (isNaN(selectedProfId)) { continue; }
        selectedProficiencyIds.push(selectedProfId);
      }
    }

    const profOptions = this.proficiencies.filter((prof) => {
      return !selectedProficiencyIds.includes(prof.id);
    });

    return searchList<Proficiency>(
      term,
      profOptions.map((proficiency) => {
        return {
          value: proficiency,
          searchableText: proficiency.name
        };
      })
    );
  }

  private searchAssociations(term: string): Association[] {
    if (term.trim().length === 0) { return []; }

    // Only search associations which have not already been selected.
    const selectedAssocIds = [];
    if (this.filters.has_association_memberships) {
      for (const selectedAssocIdStr of this.filters.has_association_memberships.split(',')) {
        const selectedAssocId = parseInt(selectedAssocIdStr, 10);
        if (isNaN(selectedAssocId)) { continue; }
        selectedAssocIds.push(selectedAssocId);
      }
    }

    const assocOptions = this.associations.filter((assoc) => {
      return !selectedAssocIds.includes(assoc.id);
    });

    return searchList<Association>(
      term,
      assocOptions.map((assoc) => {
        return {
          value: assoc,
          searchableText: assoc.name
        };
      })
    );
  }

  private searchChallenges(term: string): Challenge[] {
    return searchList<Challenge>(
      term,
      this.challenges.map((challenge) => {
        return {
          value: challenge,
          searchableText: challenge.name
        };
      })
    );
  }

  private extractReservationFieldsFromFilters() {
    this.clearReservationFields();

    if (!this.filters.has_event_type_reservation) {
      return;
    }

    const filterParts = this.filters.has_event_type_reservation.split(':::');

    const eventTypeId = parseInt(filterParts[0], 10);
    if (!isNaN(eventTypeId)) {
      for (const eventType of this.eventTypes) {
        if (eventType.id === eventTypeId) {
          this.hasReservationEventType = eventType;
        }
      }
    }

    if (filterParts.length >= 2) {
      this.hasReservationMinDate = filterParts[1];
    }
    if (filterParts.length >= 3) {
      this.hasReservationMaxDate = filterParts[2];
    }
  }

  private extractCheckInFieldsFromFilters() {
    this.clearCheckInFields();

    if (!this.filters.had_check_in_during_date_range) {
      return;
    }

    const filterParts = this.filters.had_check_in_during_date_range.split(':::');

    this.hadCheckInMinDate = filterParts[0];

    if (filterParts.length >= 2) {
      this.hadCheckInMaxDate = filterParts[1];
    }
    if (filterParts.length >= 3) {
      if (propExistsAndIsNonEmpty(filterParts[2])) {
        this.hadCheckInEntryMethod = filterParts[2] as CheckInEntryMethod;
      }
    }
  }

  private extractChallengeFieldsFromFilters() {
    this.clearChallengeFields();

    if (!propExistsAndIsNonEmpty(this.filters.has_challenge)) {
      return;
    }

    const filterParts = this.filters.has_challenge.split(':::');

    // The first part is a comma-separated list of challenge IDs (historically
    // a single ID, which parses as a one-element list). IDs with no loaded
    // challenge are kept as "unresolved" so a modal round-trip can't silently
    // drop them from the filter.
    for (const challengeIdStr of filterParts[0].split(',')) {
      const challengeId = parseInt(challengeIdStr, 10);
      if (isNaN(challengeId)) { continue; }

      const challenge = this.challenges.find(
        loadedChallenge => loadedChallenge.id === challengeId
      );
      if (challenge) {
        this.addChallenge(challenge);
      } else if (!this.hasChallengeUnresolvedIds.includes(challengeId)) {
        this.hasChallengeUnresolvedIds = [
          ...this.hasChallengeUnresolvedIds, challengeId
        ];
      }
    }

    this.hasChallengeMatchMode = (
      this.filters.has_challenge_match === FilterMatchMode.any
        ? FilterMatchMode.any : FilterMatchMode.all
    );

    if (filterParts.length >= 2) {
      const isCompleteText = filterParts[1].toLowerCase();
      if (isCompleteText === 'true') { this.hasChallengeIsComplete = true; }
      else if (isCompleteText === 'false') { this.hasChallengeIsComplete = false; }
    }
    if (filterParts.length >= 3) {
      this.hasChallengeMinCompletedDate = filterParts[2];
    }
    if (filterParts.length >= 4) {
      this.hasChallengeMaxCompletedDate = filterParts[3];
    }
  }

  private loadReservationFieldsIntoFilters() {
    const noFields = (
      (!this.hasReservationEventType) &&
      (!this.hasReservationMinDate) &&
      (!this.hasReservationMaxDate)
    )

    if (noFields) {
      this.filters.has_event_type_reservation = undefined
      return;
    }

    if (!this.hasReservationEventType) {
      this.filters.has_event_type_reservation = '';
    } else {
      this.filters.has_event_type_reservation = this.hasReservationEventType.id.toString();
    }

    if (this.hasReservationMinDate || this.hasReservationMaxDate) {
      this.filters.has_event_type_reservation += ':::' + this.hasReservationMinDate;
      this.filters.has_event_type_reservation += ':::' + this.hasReservationMaxDate;
    }
  }

  private loadCheckInFieldsIntoFilters() {
    const noFields = (
      (!this.hadCheckInMinDate) &&
      (!this.hadCheckInMaxDate) &&
      (!this.hadCheckInEntryMethod)
    )

    if (noFields) {
      this.filters.had_check_in_during_date_range = undefined
      return;
    }

    this.filters.had_check_in_during_date_range = (
      this.hadCheckInMinDate +
      ':::' +
      this.hadCheckInMaxDate
    );
    if (this.hadCheckInEntryMethod) {
      this.filters.had_check_in_during_date_range += ':::' + this.hadCheckInEntryMethod;
    }
  }

  private loadChallengeFieldsIntoFilters() {
    const selectedChallengeIds = [
      ...this.hasChallengeTypes.map(challenge => challenge.id),
      ...this.hasChallengeUnresolvedIds,
    ];

    if (selectedChallengeIds.length === 0) {
      this.filters.has_challenge = undefined;
      this.filters.has_challenge_match = undefined;
      return;
    }

    this.filters.has_challenge = selectedChallengeIds.join(',');
    // "all" is the backend default, so it doesn't need to travel as a query
    // parameter (and shouldn't clutter saved views / shared URLs).
    this.filters.has_challenge_match = (
      this.hasChallengeMatchMode === FilterMatchMode.any
        ? FilterMatchMode.any : undefined
    );

    if (this.hasChallengeIsComplete === true) {
      this.filters.has_challenge += ':::' + 'true';
    } else if (this.hasChallengeIsComplete === false) {
      this.filters.has_challenge += ':::' + 'false';
    } else {
      return;
    }

    const hasCompletedDateRange = (
      (this.hasChallengeIsComplete === true) &&
      (this.hasChallengeMinCompletedDate || this.hasChallengeMaxCompletedDate)
    );
    if (hasCompletedDateRange) {
      this.filters.has_challenge += ':::' + this.hasChallengeMinCompletedDate;
      this.filters.has_challenge += ':::' + this.hasChallengeMaxCompletedDate;
    }
  }

}
